import React, { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import {
  FaCheckDouble,
  FaEnvelope,
  FaEnvelopeOpen,
  FaReply,
  FaTrash,
} from "react-icons/fa";

import {
  getMessagesByUser,
  deleteMessageById,
  markMessageAsRead,
  markAllMessagesAsRead,
} from "../../../services/messageServices";
import { useMessage } from "../../../hooks/MessageContext";
import CreateResponseMessageForm from "./CreateResponseMessageForm";

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("fr-FR", {
    year: "2-digit",
    month: "2-digit",
    day: "2-digit",
  });

const fmtTime = (iso) =>
  new Date(iso).toLocaleTimeString("fr-FR", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

function isUnreadMessage(message) {
  return (
    !message.read_message ||
    message.read_message === 0 ||
    message.read_message === "0"
  );
}

function computeUnread(list) {
  return (list || []).reduce(
    (count, message) => count + (isUnreadMessage(message) ? 1 : 0),
    0
  );
}

function IconButton({ icon: Icon, label, onClick, disabled, tone = "slate" }) {
  const tones = {
    slate:
      "bg-slate-100 text-slate-700 ring-slate-200 hover:bg-slate-200 dark:bg-slate-900 dark:text-slate-200 dark:ring-slate-700 dark:hover:bg-slate-800",
    violet:
      "bg-violet-50 text-violet-700 ring-violet-200 hover:bg-violet-100 dark:bg-violet-500/10 dark:text-violet-300 dark:ring-violet-500/20",
    emerald:
      "bg-emerald-50 text-emerald-700 ring-emerald-200 hover:bg-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20",
    red: "bg-red-50 text-red-700 ring-red-200 hover:bg-red-100 dark:bg-red-500/10 dark:text-red-300 dark:ring-red-500/20",
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={[
        "inline-flex min-h-10 items-center justify-center gap-2 rounded-2xl px-3 text-xs font-bold ring-1 transition disabled:cursor-not-allowed disabled:opacity-50",
        tones[tone] || tones.slate,
      ].join(" ")}
      title={label}
    >
      <Icon />
      <span className="hidden xs:inline sm:inline">{label}</span>
    </button>
  );
}

export default function MessagesReceived({
  userId,
  refresh,
  setRefresh,
  onCounts,
}) {
  const [messages, setMessages] = useState([]);
  const [replyMessageId, setReplyMessageId] = useState(null);
  const [busyAllRead, setBusyAllRead] = useState(false);
  const [loading, setLoading] = useState(false);

  const { setUnreadMessageCount } = useMessage();

  const mountedRef = useRef(true);
  const isFetchingRef = useRef(false);
  const onCountsRef = useRef(onCounts);
  const setUnreadRef = useRef(setUnreadMessageCount);
  const lastCountsRef = useRef({ total: null, unread: null });

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    onCountsRef.current = onCounts;
  }, [onCounts]);

  useEffect(() => {
    setUnreadRef.current = setUnreadMessageCount;
  }, [setUnreadMessageCount]);

  const pushCountsIfChanged = useCallback((list) => {
    const total = list.length;
    const unread = computeUnread(list);
    const previous = lastCountsRef.current;

    if (previous.total !== total || previous.unread !== unread) {
      lastCountsRef.current = { total, unread };
      onCountsRef.current?.({ total, unread });
      setUnreadRef.current?.(unread);
    }
  }, []);

  const loadMessages = useCallback(async () => {
    if (!userId || isFetchingRef.current) return;

    isFetchingRef.current = true;
    setLoading(true);

    try {
      const data = await getMessagesByUser(userId);
      const filtered = (data || []).filter((message) => !message.is_copy);

      if (!mountedRef.current) return;

      setMessages(filtered);
      pushCountsIfChanged(filtered);
    } catch (error) {
      console.error("[MessagesReceived] fetch error:", error);
      toast.error("Erreur lors du chargement des messages.");
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }

      isFetchingRef.current = false;
    }
  }, [pushCountsIfChanged, userId]);

  useEffect(() => {
    loadMessages();
  }, [loadMessages]);

  useEffect(() => {
    if (!refresh) return;

    setRefresh?.(false);
    loadMessages();
  }, [loadMessages, refresh, setRefresh]);

  const unread = computeUnread(messages);

  const handleDeleteMessage = async (messageId) => {
    const confirmDelete = window.confirm("Supprimer ce message ?");

    if (!confirmDelete) return;

    try {
      await deleteMessageById(messageId);

      const next = messages.filter((message) => message.id !== messageId);

      setMessages(next);
      pushCountsIfChanged(next);

      toast.success("Message supprimé avec succès.");
    } catch (error) {
      console.error("Erreur suppression message:", error);
      toast.error("Erreur lors de la suppression du message.");
    }
  };

  const handleMarkAsRead = async (messageId) => {
    try {
      const target = messages.find((message) => message.id === messageId);

      if (!target || !isUnreadMessage(target)) return;

      await markMessageAsRead(messageId);

      const next = messages.map((message) =>
        message.id === messageId
          ? {
              ...message,
              read_message: true,
            }
          : message
      );

      setMessages(next);
      pushCountsIfChanged(next);
    } catch (error) {
      console.error("Erreur marquage lu:", error);
      toast.error("Échec du marquage en lu.");
    }
  };

  const handleMarkAsUnreadLocal = (messageId) => {
    const next = messages.map((message) =>
      message.id === messageId
        ? {
            ...message,
            read_message: false,
          }
        : message
    );

    setMessages(next);
    pushCountsIfChanged(next);
  };

  const handleMarkAllAsRead = async () => {
    if (busyAllRead || messages.length === 0) return;

    try {
      setBusyAllRead(true);

      await markAllMessagesAsRead(userId);

      const next = messages.map((message) => ({
        ...message,
        read_message: true,
      }));

      setMessages(next);
      pushCountsIfChanged(next);

      toast.success("Tous les messages ont été marqués comme lus.");
    } catch (error) {
      console.error("Erreur marquer tout lu:", error);
      toast.error("Échec du marquage global.");
    } finally {
      setBusyAllRead(false);
    }
  };

  if (loading && messages.length === 0) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, index) => (
          <div
            key={index}
            className="h-36 animate-pulse rounded-[1.5rem] bg-slate-100 dark:bg-slate-800"
          />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="relative flex h-11 w-11 items-center justify-center rounded-2xl bg-violet-100 text-violet-700 dark:bg-violet-500/10 dark:text-violet-300">
            <FaEnvelope />
            {unread > 0 && (
              <span className="absolute -right-2 -top-2 inline-flex min-w-6 items-center justify-center rounded-full bg-violet-600 px-2 py-1 text-xs font-bold text-white">
                {unread}
              </span>
            )}
          </div>

          <div>
            <p className="font-bold text-slate-950 dark:text-white">
              Messages reçus
            </p>

            <p className="text-xs text-slate-500 dark:text-slate-400">
              {messages.length} message{messages.length > 1 ? "s" : ""} ·{" "}
              {unread} non lu{unread > 1 ? "s" : ""}
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={handleMarkAllAsRead}
          disabled={busyAllRead || messages.length === 0}
          className="inline-flex min-h-11 items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-200 dark:hover:bg-slate-900"
        >
          <FaCheckDouble />
          Tout marquer lu
        </button>
      </div>

      {messages.length === 0 ? (
        <div className="flex min-h-[260px] items-center justify-center rounded-[1.75rem] border border-dashed border-slate-300 bg-slate-50 p-6 text-center dark:border-slate-700 dark:bg-slate-950/50">
          <div>
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-violet-600 shadow-md dark:bg-slate-900">
              <FaEnvelope />
            </div>

            <h4 className="mt-4 text-lg font-bold text-slate-950 dark:text-white">
              Aucun message reçu
            </h4>

            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
              Ta boîte de réception est vide pour le moment.
            </p>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {messages.map((message) => {
            const isUnread = isUnreadMessage(message);
            const senderName = `${message.Sender?.nom || ""} ${
              message.Sender?.prenom || ""
            }`.trim();

            return (
              <article
                key={message.id}
                className={[
                  "overflow-hidden rounded-[1.5rem] border p-4 shadow-lg transition dark:shadow-black/10",
                  isUnread
                    ? "border-violet-200 bg-violet-50 shadow-violet-100/70 dark:border-violet-800/80 dark:bg-violet-950/30"
                    : "border-slate-200 bg-white shadow-slate-200/60 dark:border-slate-800 dark:bg-slate-950",
                ].join(" ")}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span
                        className={[
                          "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-2xl",
                          isUnread
                            ? "bg-violet-600 text-white"
                            : "bg-slate-100 text-slate-500 dark:bg-slate-900 dark:text-slate-300",
                        ].join(" ")}
                      >
                        {isUnread ? <FaEnvelope /> : <FaEnvelopeOpen />}
                      </span>

                      <div className="min-w-0">
                        <p className="truncate text-sm font-bold text-slate-950 dark:text-white">
                          {senderName || "Expéditeur inconnu"}
                        </p>

                        <p className="text-xs text-slate-500 dark:text-slate-400">
                          {fmtDate(message.created_at)} ·{" "}
                          {fmtTime(message.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {isUnread && (
                    <span className="shrink-0 rounded-full bg-violet-600 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white">
                      Nouveau
                    </span>
                  )}
                </div>

                <h4 className="mt-3 text-base font-bold text-blue-700 dark:text-blue-300">
                  {message.subject}
                </h4>

                <div
                  className="prose prose-sm mt-2 max-w-none text-sm leading-6 text-slate-700 dark:prose-invert dark:text-slate-200"
                  dangerouslySetInnerHTML={{ __html: message.content }}
                />

                <div className="mt-4 grid grid-cols-3 gap-2">
                  <IconButton
                    icon={FaReply}
                    label="Répondre"
                    tone="violet"
                    onClick={() => setReplyMessageId(message.id)}
                  />

                  {isUnread ? (
                    <IconButton
                      icon={FaEnvelopeOpen}
                      label="Lu"
                      tone="emerald"
                      onClick={() => handleMarkAsRead(message.id)}
                    />
                  ) : (
                    <IconButton
                      icon={FaEnvelope}
                      label="Non lu"
                      tone="slate"
                      onClick={() => handleMarkAsUnreadLocal(message.id)}
                    />
                  )}

                  <IconButton
                    icon={FaTrash}
                    label="Supprimer"
                    tone="red"
                    onClick={() => handleDeleteMessage(message.id)}
                  />
                </div>

                {replyMessageId === message.id && (
                  <div className="mt-4">
                    <CreateResponseMessageForm
                      show
                      onHide={() => setReplyMessageId(null)}
                      onSuccess={() => {
                        setReplyMessageId(null);
                        setRefresh?.(true);
                      }}
                      userId={userId}
                      initialRecipientId={message.sender_id}
                      initialSubject={`RE: ${message.subject}`}
                    />
                  </div>
                )}
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}